// «Nouveau processus» — three steps, not a mega-form (ux-design §8, door 2):
// Quoi → Où (pre-selected from the tree node you were on) → Qui/Quand.
// Only the name is mandatory; the rest is filled while working.
import { useEffect, useMemo, useState } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';
import { PROCESS_TIERS, processTiers } from '../../shared/repoLayout';

const TIERS = PROCESS_TIERS;

export function NewProcessModal({ defaultNode, onClose, onCreated }: {
  defaultNode?: string;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const { t } = useT();
  const [step, setStep] = useState(0);
  const [people, setPeople] = useState<any[]>([]);
  const [processes, setProcesses] = useState<any[]>([]);
  const [index, setIndex] = useState<any | null>(null);
  const tiers = useMemo(() => processTiers(index?.entries ?? []), [index]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nodeParts = (defaultNode ?? '').replace(/\/\/$/, '').split('/').filter(Boolean);
  const [form, setForm] = useState({
    name: '', kind: 'new' as 'new' | 'rework', reworks_process_id: null as number | null,
    description: '', goal: '',
    git_tier: nodeParts[0] ?? TIERS[2], git_area: nodeParts.slice(1).join('/'),
    git_path: '' as string,
    client: '', consultant_person_id: null as number | null, process_owner_person_id: null as number | null,
    horizon_start: '', horizon_end: '', system_impact: 'none' as string,
  });

  useEffect(() => {
    api.getPeople().then(setPeople).catch(() => {});
    api.getProcesses().then(setProcesses).catch(() => {});
    api.getRepoIndex().then(idx => {
      setIndex(idx);
      const actual = processTiers(idx.entries);
      setForm(f => ({ ...f, git_tier: actual.find(tier => tier[0] === f.git_tier[0]) ?? f.git_tier }));
    }).catch(() => {});
  }, []);

  const areaOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of index?.entries ?? []) {
      if (e.tier !== form.git_tier) continue;
      for (let i = 1; i <= e.areaSegments.length; i++) set.add(e.areaSegments.slice(0, i).join('/'));
    }
    return [...set].sort();
  }, [index, form.git_tier]);

  const fileOptions = useMemo(() =>
    (index?.entries ?? []).filter((e: any) =>
      e.tier === form.git_tier && (!form.git_area || e.areaSegments.join('/') === form.git_area)),
    [index, form.git_tier, form.git_area]);

  const submit = async () => {
    if (!form.name.trim()) { setError(t('fiche.p1.title') + ' : nom requis'); setStep(0); return; }
    setSaving(true);
    setError(null);
    try {
      const created = await api.createProcess({
        name: form.name, kind: form.kind,
        reworks_process_id: form.kind === 'rework' ? form.reworks_process_id : null,
        description: form.description || null, goal: form.goal || null,
        client: form.client || null,
        consultant_person_id: form.consultant_person_id, process_owner_person_id: form.process_owner_person_id,
        horizon_start: form.horizon_start || null, horizon_end: form.horizon_end || null,
        system_impact: form.system_impact,
      });
      await api.updateProcess(created.id, { git_tier: form.git_tier, git_area: form.git_area || null });
      if (form.git_path) await api.linkRepoFile(created.id, form.git_path);
      onCreated(created.id);
    } catch (e: any) {
      setError(e.message);
      setSaving(false);
    }
  };

  const stepTitles = ['Quoi', 'Où', 'Qui / Quand'];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{t('rep.new')} — {stepTitles[step]}</h3>
          <button className="close-btn" onClick={onClose}><X className="icon-sm" /></button>
        </div>
        <div className="modal-body">
          {step === 0 && (
            <div className="flex flex-col gap-12">
              <div className="form-group">
                <label className="form-label">Nom</label>
                <input className="form-input" autoFocus value={form.name}
                       onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('fiche.p1.kind')}</label>
                <div className="selectable-group">
                  {(['new', 'rework'] as const).map(k => (
                    <button key={k} type="button" className={`selectable${form.kind === k ? ' active' : ''}`}
                            onClick={() => setForm({ ...form, kind: k })}>
                      {t(`fiche.p1.kind.${k}`)}
                    </button>
                  ))}
                </div>
              </div>
              {form.kind === 'rework' && (
                <div className="form-group">
                  <label className="form-label">{t('fiche.p1.reworks')}</label>
                  <select className="form-select" value={form.reworks_process_id ?? ''}
                          onChange={e => setForm({ ...form, reworks_process_id: Number(e.target.value) || null })}>
                    <option value="">—</option>
                    {processes.filter(p => p.state === 'live' || p.in_inventory)
                      .map(p => <option key={p.id} value={p.id}>{p.process_code} — {p.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">{t('fiche.p1.description')}</label>
                <textarea className="form-textarea" rows={2} value={form.description}
                          onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('fiche.p1.goal')}</label>
                <textarea className="form-textarea" rows={2} value={form.goal}
                          onChange={e => setForm({ ...form, goal: e.target.value })} />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="flex flex-col gap-12">
              <div className="form-group">
                <label className="form-label">Tier</label>
                <div className="selectable-group">
                  {tiers.map(tier => (
                    <button key={tier} type="button" className={`selectable${form.git_tier === tier ? ' active' : ''}`}
                            onClick={() => setForm({ ...form, git_tier: tier, git_area: '', git_path: '' })}>
                      {tier.slice(0, 1)} · {tier.replace(/^\d+ /, '')}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Domaine</label>
                <select className="form-select" value={form.git_area}
                        onChange={e => setForm({ ...form, git_area: e.target.value, git_path: '' })}>
                  <option value="">{t('rep.root')}</option>
                  {areaOptions.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('fiche.diagramPick')} <span className="text-ink-3">(optionnel)</span></label>
                <select className="form-select" value={form.git_path}
                        onChange={e => setForm({ ...form, git_path: e.target.value })}>
                  <option value="">— plus tard —</option>
                  {fileOptions.map((f: any) => <option key={f.path} value={f.path}>{f.title}{f.variant ? ` (${f.variant})` : ''}{f.isEmpty ? ' — vide' : ''}</option>)}
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="flex flex-col gap-12">
              <div className="form-group">
                <label className="form-label">{t('fiche.p1.client')}</label>
                <input className="form-input" value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} />
              </div>
              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label">{t('fiche.p1.consultant')}</label>
                  <select className="form-select" value={form.consultant_person_id ?? ''}
                          onChange={e => setForm({ ...form, consultant_person_id: Number(e.target.value) || null })}>
                    <option value="">—</option>
                    {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">{t('fiche.p1.owner')}</label>
                  <select className="form-select" value={form.process_owner_person_id ?? ''}
                          onChange={e => setForm({ ...form, process_owner_person_id: Number(e.target.value) || null })}>
                    <option value="">—</option>
                    {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-12">
                <div className="form-group flex-1">
                  <label className="form-label">{t('fiche.p1.horizonStart')}</label>
                  <input type="date" className="form-input" value={form.horizon_start}
                         onChange={e => setForm({ ...form, horizon_start: e.target.value })} />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">{t('fiche.p1.horizonEnd')}</label>
                  <input type="date" className="form-input" value={form.horizon_end}
                         onChange={e => setForm({ ...form, horizon_end: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('fiche.p1.systemImpact')}</label>
                <div className="selectable-group">
                  {(['none', 'new_system', 'adapt_system'] as const).map(k => (
                    <button key={k} type="button" className={`selectable${form.system_impact === k ? ' active' : ''}`}
                            onClick={() => setForm({ ...form, system_impact: k })}>
                      {t(`fiche.p1.impact.${k}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && <div className="text-err text-sm mt-12">{error}</div>}
        </div>
        <div className="modal-footer">
          {step > 0 && (
            <button className="btn btn-ghost" onClick={() => setStep(step - 1)}>
              <ChevronLeft className="icon-sm" /> {t('common.back')}
            </button>
          )}
          <span style={{ marginLeft: 'auto' }} />
          <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          {step < 2 ? (
            <button className="btn btn-accent" disabled={step === 0 && !form.name.trim()} onClick={() => setStep(step + 1)}>
              {stepTitles[step + 1]} <ChevronRight className="icon-sm" />
            </button>
          ) : (
            <button className="btn btn-accent" disabled={saving} onClick={submit}>{t('common.save')}</button>
          )}
        </div>
      </div>
    </div>
  );
}
